<template>
  <div
    ref="container"
    v-on-clickaway="close"
    tabindex="0"
    class="nortic-select"
    :class="{ 'is-disabled': isDisabled }"
    @keydown.down.prevent="listDownKey"
    @keydown.up.prevent="listUpKey"
    @keydown.esc="close"
    @keydown.enter="listEnterKey"
    @keydown.space="toggleHighlighted"
    @keydown.tab="close"
  >
    <div
      class="selected validate-input"
      :class="isOpenClass"
      @click="toggle"
    >
      <span class="text">
        <slot
          name="selected"
          :currentValue="innerValue"
          :placeholder="placeholder"
        >
          <span
            v-if="showPlaceholder"
            class="placeholder"
          >
            {{ placeholder }}
          </span>
          <span v-else>
            {{ selectedLabel }}
          </span>
        </slot>
      </span>
      <span class="icon">
        <SvgIcon
          :data="require('@icons/streamline-icons/Outline version/52-Arrows-Diagrams/01-Arrows/arrow-down-1.svg')"
          :class="isOpenClass"
        />
      </span>
    </div>
    <transition name="fade">
      <div
        v-show="isOpen"
        ref="list"
        class="list"
        :class="upOrDownClass"
      >
        <div class="header">
          <slot
            name="header"
            :is-open="isOpen"
            :update-filter="updateFilter"
            :items="items"
          >
            <div
              v-if="isFilterable && isOpen"
              class="search-box"
            >
              <TextInput
                v-model="filterValue"
                :is-validator-box-visible="false"
                :placeholder="filterPlaceholder"
                should-autofocus
                @keydown.space.stop
                @keydown.tab.stop
              />
            </div>
          </slot>
        </div>
        <div
          ref="scrollList"
          class="list-container"
          :style="listContainerMaxHeight"
          tabindex="0"
        >
          <slot
            name="list"
            :is-open="isOpen"
            :items="items"
            :filter-value="filterValue"
          >
            <NorticSelectItem
              v-for="(option, index) in options"
              :key="index"
              :value="getOptionValue(option)"
              :label="getOptionLabel(option)"
            />
          </slot>
        </div>
        <div>
          <slot
            name="footer"
            :is-open="isOpen"
            :update-filter="updateFilter"
            :items="items"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2';
import NorticSelectItem from '@/components/common/input/nortic-select/nortic-select-item/NorticSelectItem.vue';
import dynamicCustomInput from '@/components/common/input/mixins/dynamicCustomInput';
import TextInput from '@/components/common/input/text-input/NorticTextInput.vue';
import validator from '@/components/common/input/mixins/validator';
import openUpOrDown from '@/mixins/openUpOrDown';
import difference from 'lodash/difference';
import union from 'lodash/union';
import isArray from 'lodash/isArray';

const isOptionAnObject = (option) => typeof option === 'object' && option.constructor === Object;

const defaultFilterFunction = (filterValue, itemValue, itemLabel) => `${itemValue}`.toLowerCase()
  .includes(filterValue.toLowerCase())
  || itemLabel.toLowerCase().includes(filterValue.toLowerCase());

export default {
  name: 'NorticSelect',
  components: { TextInput, NorticSelectItem },
  mixins: [
    clickaway,
    dynamicCustomInput({ autoEmit: true, event: 'change' }),
    validator,
    openUpOrDown,
  ],
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    isFilterable: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Function,
      default: defaultFilterFunction,
    },
    filterPlaceholder: {
      type: String,
      default: 'Filtrera',
    },
    placeholder: {
      type: String,
      default: null,
    },
    filterEventFunction: {
      type: Function,
      default: (value) => value,
    },
    listMaxHeightNrOfItems: {
      type: Number,
      default: 5,
    },
    listMaxHeightItemHeight: {
      type: String,
      default: '1em',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    canMultiselect: {
      type: Boolean,
      default: false,
    },
    multiselectLabel: {
      type: Function,
      default: (values) => (values.length > 0
        ? `${values.length} valda`
        : 'Inget valt'),
    },
  },
  data() {
    return {
      isOpen: false,
      highlightedItem: null,
      filterValue: '',
      items: [],
    };
  },
  computed: {
    selectedLabel() {
      if (!this.isMultiselect) {
        return this.items
          .filter((item) => item.isSelected)
          .map((item) => item.label)[0];
      }

      return this.multiselectLabel(this.items.filter((item) => !item?.isGroup && item?.isSelected));
    },
    highlightableItems() {
      return this.items
        .filter((item) => !item.isDisabled && item.isFiltered)
        .map((item) => item);
    },
    showPlaceholder() {
      return this.placeholder && (this.selectedLabel === null || this.selectedLabel === undefined);
    },
    filteredItemsLength() {
      return this.items
        .filter((item) => item.isFiltered)
        .length;
    },
    isOpenClass() {
      return {
        'is-open': this.isOpen,
        ...this.problemClasses,
      };
    },
    listContainerMaxHeight() {
      return {
        maxHeight: `calc(${this.listMaxHeightNrOfItems} * (${this.listMaxHeightItemHeight} + 23px) - 1px)`,
      };
    },
    isMultiselect() {
      return this.canMultiselect && isArray(this.innerValue);
    },
    areHighlightedValuesSelected() {
      return this.innerValue?.includes(this.highlightedItem?.value)
        || (this.highlightedItem?.isGroup && isArray(this.highlightedItem?.value) && difference(this.highlightedItem?.value, this.innerValue).length === 0);
    },
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.setDirection(this.$refs.container, this.$refs.list);
      }
    },
  },
  methods: {
    updateFilter(value) {
      this.filterValue = this.filterEventFunction(value);
    },
    toggle() {
      if (!this.isDisabled) {
        this.isOpen = !this.isOpen;
      }
    },
    getOptionValue(option) {
      if (isOptionAnObject(option) && (option.value !== undefined && option.value !== null)) {
        return option.value;
      }

      return option;
    },
    getOptionLabel(option) {
      if (isOptionAnObject(option) && option.label) {
        return option.label;
      }

      return null;
    },
    listDownKey() {
      if (!this.isDisabled) {
        this.isOpen = true;
        this.highlightedItem = this.highlightableItems[this.getDownKeyNewHighlightedIndex()];
      }
    },
    listUpKey() {
      if (!this.isDisabled) {
        this.isOpen = true;
        this.highlightedItem = this.highlightableItems[this.getUpKeyNewHighlightedIndex()];
      }
    },
    listEnterKey() {
      if (!this.isOpen) {
        this.toggle();
        return;
      }

      if (this.isMultiselect) {
        this.toggleHighlighted();
      } else {
        this.selectHighlighted();
      }
    },
    toggleHighlighted() {
      if (!this.isDisabled && this.isOpen && this.isMultiselect) {
        if (this.areHighlightedValuesSelected) {
          this.removeHighlighted();
        } else {
          this.addHighlighted();
        }
      }
    },
    addHighlighted() {
      if (this.highlightedItem) {
        if (this.highlightedItem.isGroup && isArray(this.highlightedItem.value)) {
          this.innerValue = union(this.innerValue, this.highlightedItem.value);
        } else {
          this.innerValue.push(this.highlightedItem.value);
        }

        this.$emit('checked', this.highlightedItem);
      }
    },
    removeHighlighted() {
      if (this.highlightedItem) {
        if (this.highlightedItem.isGroup && isArray(this.highlightedItem.value)) {
          this.innerValue = difference(this.innerValue, this.highlightedItem.value);
        } else {
          this.innerValue = this.innerValue.filter((val) => val !== this.highlightedItem.value);
        }

        this.$emit('unchecked', this.highlightedItem);
      }
    },
    selectHighlighted() {
      if (!this.isDisabled && this.isOpen && !this.isMultiselect) {
        this.innerValue = this.highlightedItem.value;
        this.toggle();
      }
    },
    getDownKeyNewHighlightedIndex() {
      const highlightedIndex = this.highlightableItems.indexOf(this.highlightedItem) + 1;
      return highlightedIndex >= this.highlightableItems.length
        ? 0
        : highlightedIndex;
    },
    getUpKeyNewHighlightedIndex() {
      const highlightedIndex = this.highlightableItems.indexOf(this.highlightedItem) - 1;
      return highlightedIndex < 0
        ? this.highlightableItems.length - 1
        : highlightedIndex;
    },
    close() {
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@/assets/styles/transitions/fade';
  @import '~@/components/common/input/style/_style.scss';

  @include fade;

  .nortic-select {
    &.is-disabled {
      opacity: 0.3;

      .selected {
        cursor: default;
      }
    }

    &:not(.is-disabled) {
      &:focus,
      &:focus-within {
        .selected {
          box-shadow: inset 0 0 2px 0 var(--input-active-border-hex);
          background: var(--input-active-bg-hex);

          .icon {
            border-color: var(--input-active-border-hex);

            svg {
              color: var(--input-active-border-hex);
            }
          }
        }
      }
    }

    position: relative;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    .selected {
      &.is-open {
        .icon {
          border-color: var(--input-active-border-hex);

          svg {
            color: var(--input-active-border-hex);
          }
        }
      }

      &:hover {
        .icon {
          border-color: var(--input-hover-border-hex);
        }
      }

      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      white-space: nowrap;
      border-radius: 3px;
      padding: 0 10px;
      transition: border 0.3s;
      cursor: pointer;
      height: 40px;

      .text {
        color: var(--primary-text-hex);
        overflow: hidden;
        text-overflow: ellipsis;

        .placeholder {
          color: var(--primary-text-discrete-hex);
        }
      }

      .icon {
        position: relative;
        font-size: 24px;
        display: flex;
        padding-left: 9px;
        border-left: 1px solid var(--input-border-hex);

        svg {
          &.is-open {
            transform: rotate(-180deg);
          }

          transition: transform 0.3s linear;
        }
      }
    }

    .list {
      &.up {
        bottom: calc(1em + 26px);
        border-radius: 6px 6px 0 0;
      }

      &.down {
        top: calc(1em + 26px);
        border-radius: 0 0 6px 6px;
      }

      position: absolute;
      display: grid;
      left: 1px;
      width: 100%;
      background: var(--primary-bg-hex);
      box-shadow: 0 0 3px 0 var(--shadow-color-hex);
      z-index: 99999;
      overflow: hidden;

      .header {
        z-index: 1;
        box-shadow: 0 2px 7px 5px rgba(var(--shadow-color-rgb), 0.08);

        .search-box {
          padding: 10px 10px;
          background: var(--primary-bg-hex);
        }
      }

      .list-container {
        overflow-y: auto;
        overflow-x: hidden;
      }
    }
  }
</style>
